import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Folder, Share2, Lock, ExternalLink, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicFolderView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/public/folders/${token}`)
      .then(r => {
        if (!r.ok) { setErr(r.status === 404 ? 'This public link has expired or was revoked.' : 'Failed to load.'); return null; }
        return r.json();
      })
      .then(d => { if (d) setData(d); })
      .catch(() => setErr('Network error'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-emerald-950/20 dark:to-blue-950/20">
      <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
    </div>
  );

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-amber-50 dark:from-rose-950/20 dark:to-amber-950/20 p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center space-y-3">
          <Lock className="w-12 h-12 mx-auto text-rose-400" />
          <h1 className="text-xl font-semibold">Public link unavailable</h1>
          <p className="text-sm text-muted-foreground">{err}</p>
        </CardContent>
      </Card>
    </div>
  );

  const { folder, children } = data || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/30 via-background to-blue-50/30 dark:from-emerald-950/10 dark:to-blue-950/10 py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-5" data-testid="public-folder-page">
        {/* Branding */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-emerald-600 font-bold tracking-tight">CountBucks</span>
            <Badge variant="outline" className="text-[10px]">Shared folder</Badge>
          </div>
          <a href="/" className="text-xs text-muted-foreground hover:text-emerald-600 flex items-center gap-1">
            Open CountBucks <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Folder header */}
        <Card>
          <CardContent className="p-5 flex items-start gap-3">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: `${folder?.color || '#3b82f6'}20`, color: folder?.color || '#3b82f6' }}
            >
              <Folder className="w-7 h-7" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl font-semibold tracking-tight">{folder?.name}</h1>
              {folder?.description && <p className="text-sm text-muted-foreground mt-1">{folder.description}</p>}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(folder?.tags || []).map(t => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
              </div>
            </div>
            <Share2 className="w-4 h-4 text-emerald-500" title="Public" />
          </CardContent>
        </Card>

        {/* Children */}
        <Card>
          <CardContent className="p-5 space-y-2">
            <h2 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Contents ({children?.length || 0})</h2>
            {(!children || children.length === 0) ? (
              <p className="text-sm text-muted-foreground italic">This folder is empty.</p>
            ) : (
              <div className="space-y-1">
                {children.map(c => (
                  <div key={c.folder_id} className="flex items-center gap-3 p-2 rounded hover:bg-muted/40">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ background: `${c.color || '#3b82f6'}20`, color: c.color || '#3b82f6' }}
                    >
                      <Folder className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{c.name}</p>
                      {c.description && <p className="text-[11px] text-muted-foreground truncate">{c.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-[10px] text-muted-foreground">
          Read-only public link · Documents inside are not shared.
        </p>
      </div>
    </div>
  );
}
